/**
 * @file Pure upload/document logic — zero DOM dependency.
 *
 * During Vue migration:
 *   - concatDocs       → useUploadsStore().documentsMarkdown (computed)
 *   - buildImageContext → useUploadsStore().imageContext (computed)
 *   - canProceedUpload  → useUploadsStore().canProceed (computed)
 *   - getUsableImages   → useUploadsStore().usableImages (computed)
 *   - buildDocAnalysisMsg → useAnalysis().buildDocMessage()
 *   - buildQAEnrichmentMsg → useAnalysis().buildQAMessage()
 */
import type { Brief, UploadedDoc, UploadedImage } from '../types.ts';

// ── DOCUMENT HELPERS ───────────────────────────────────────────────

/**
 * Concatenate all parsed documents into a single markdown string.
 * Used as input for AI document analysis.
 */
export const concatDocs = (docs: UploadedDoc[]): string =>
  docs
    .filter(d => d.status === 'done')
    .map(d => `--- Document: ${d.name} ---\n${d.markdown}`)
    .join('\n\n');

/**
 * Build image analysis context string from analyzed images.
 * Used as supplementary input for AI calls.
 */
export const buildImageContext = (images: UploadedImage[]): string =>
  images
    .filter(i => i.analysis)
    .map(i => `Image "${i.name}": ${i.analysis!.placement} (${i.analysis!.useDecision})`)
    .join('\n');

/**
 * Get images that should be included in AI calls (useDecision === 'use').
 */
export const getUsableImages = (images: UploadedImage[]): UploadedImage[] =>
  images.filter(i => i.analysis?.useDecision === 'use');

// ── PROCEED VALIDATION ─────────────────────────────────────────────

/**
 * Check if user can proceed from the upload screen.
 * Requires: (docs or images) AND a logo.
 */
export const canProceedUpload = (
  docs: UploadedDoc[],
  images: UploadedImage[],
  hasLogo: boolean,
): boolean => {
  const hasDocs = docs.some(d => d.status === 'done');
  const hasImgs = images.length > 0;
  return (hasDocs || hasImgs) && hasLogo;
};

/**
 * Check if any images still need analysis.
 */
export const hasUnanalyzedImages = (images: UploadedImage[]): boolean =>
  images.some(i => i.analysisStatus !== 'done' && i.analysisStatus !== 'error');

/**
 * Determine next screen after upload:
 * - If docs exist → 'docview' (review documents first)
 * - Otherwise → 'concept' (generate directly)
 */
export const uploadNextScreen = (docs: UploadedDoc[]): 'docview' | 'concept' =>
  docs.some(d => d.status === 'done') ? 'docview' : 'concept';

// ── AI MESSAGE BUILDERS ────────────────────────────────────────────

/**
 * Build the user message for document analysis AI call.
 * Assembles documents, image context, and user notes into XML-structured prompt.
 */
export const buildDocAnalysisMessage = (
  docs: UploadedDoc[],
  images: UploadedImage[],
  docNotes: string,
): string => {
  const docsText = concatDocs(docs);
  const imgContext = buildImageContext(images);

  return [
    docsText ? `<documents>\n${docsText}\n</documents>` : '',
    imgContext ? `<image_analysis>\n${imgContext}\n</image_analysis>` : '',
    docNotes ? `<user_notes>${docNotes}</user_notes>` : '',
  ]
    .filter(Boolean)
    .join('\n\n');
};

/**
 * Build the user message for Q&A enrichment AI call.
 * Assembles brief answers, follow-up answers, and enrichment data.
 */
export const buildQAEnrichmentMessage = (
  brief: Brief,
  followUps: Array<{ question: string; answer: string; skipped: boolean }>,
  enrichment: Record<string, unknown>,
): string => {
  const followUpAnswers = followUps
    .filter(f => f.answer && !f.skipped)
    .map(f => `${f.question}: ${f.answer}`)
    .join('\n');

  const answerParts = [
    brief.businessDesc ? `Business: ${brief.businessDesc}` : '',
    brief.customerProblem ? `Customer problem: ${brief.customerProblem}` : '',
    brief.desiredAction ? `Desired action: ${brief.desiredAction}` : '',
    brief.actionDetail ? `Action detail: ${brief.actionDetail}` : '',
    brief.leadMagnet ? `Lead magnet: ${brief.leadMagnet}` : '',
    brief.vibes?.length ? `Vibes: ${brief.vibes.join(', ')}` : '',
    brief.refsite ? `Reference site: ${brief.refsite}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  const extraParts = [
    followUpAnswers ? `<follow_up_answers>\n${followUpAnswers}\n</follow_up_answers>` : '',
    (enrichment as Record<string, string>).socialProof
      ? `<social_proof>${(enrichment as Record<string, string>).socialProof}</social_proof>`
      : '',
    (enrichment as Record<string, string[]>).objections?.length
      ? `<objections>${JSON.stringify((enrichment as Record<string, string[]>).objections)}</objections>`
      : '',
  ]
    .filter(Boolean)
    .join('\n\n');

  return `<qa_answers>\n${answerParts}\n</qa_answers>${extraParts ? `\n\n${extraParts}` : ''}`;
};

/**
 * Build context note for concept generation from uploads.
 * Assembles documents, image context, and user notes.
 */
export const buildUploadConceptNote = (
  docs: UploadedDoc[],
  images: UploadedImage[],
  docNotes: string,
  additionalNotes: string,
): string => {
  const docsText = concatDocs(docs);
  const imgContext = buildImageContext(images);

  return [
    docsText ? `UPLOADED DOCUMENTS:\n${docsText}` : '',
    imgContext ? `PROVIDED IMAGES:\n${imgContext}` : '',
    docNotes ? `ADDITIONAL NOTES:\n${docNotes}` : '',
    additionalNotes ? `CONTEXT: ${additionalNotes}` : '',
  ]
    .filter(Boolean)
    .join('\n\n');
};
